import { useContext, useState, useEffect, useCallback, ChangeEvent, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    FormLabel,
    FormGroup,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { JSONSchema7 } from 'json-schema';

import { Text } from '@hubdevs/viu-ui-shared';
import { ApiServiceContext } from '../../api/ApiService.context';
import {
    Attribution,
    ATTRIBUTION_TYPE_DEFAULT,
    AttributionStatus,
    AttributionVersion,
    JsonSchemaType,
} from '../../api/attribution/types';
import { AppFormRow } from '../../components/AppFormRow/AppFormRow';
import { AppFormCell } from '../../components/AppFormCell/AppFormCell';
import { AppPage } from '../../components/Page/Page';
import { AppTextField } from '../../components/AppTextField/AppTextField.component';
import { SchemaFormSection } from '../../components/SchemaForm';
import { ErrorsByPointer, validateFormData } from '../../utils/jsonSchemaValidator';
import { AttributionService } from '../../api/attribution/AttributionService.class';
import { ApiServiceCollection } from '../../api/ApiServiceCollection.class';

type LoadingState = 'NOT STARTED' | 'LOADING' | 'SUCCESS' | 'FAIL';

const ATTRIBUTION_TYPE_CHANGE_FLAG: boolean = true;

const ATTRIBUTION_SCHEMA_TYPE_MAP: Record<string, string> = {
    "AGGREGATOR": "Aggregator",
    "CAMPAIGN": "Campaign",
    "PARTNER": "Partner",
    "CAMPAIGN_MEDIUM": "Campaign Medium",
    "CAMPAIGN_SOURCE": "Campaign Source",
    "CAMPAIGN_CONTENT": "Campaign Content",
    "BORROWER": "Borrower",
    "AGENT_ID": "Agent Id",
    "AGENT_NAME": "Agent Name",
    "ORIGINATING_CAMPAIGN": "Originating Campaign"

}
const ATTRIBUTION_TYPE_MAP = Object.entries(ATTRIBUTION_SCHEMA_TYPE_MAP).reduce((acc, cur) => {
    acc[cur[1]] = cur[0];
    return acc;
}, {} as Record<string, string>);

const attributionTypeToSchemaType = (type: string) => {
    if (!ATTRIBUTION_TYPE_CHANGE_FLAG) {
        return type;
    }
    if (!ATTRIBUTION_TYPE_MAP[type]) {
        return type.toUpperCase().split(' ').join('_');
    }
    return ATTRIBUTION_TYPE_MAP[type];
};
const schemaTypeToAttributionType = (schemaType: string) => {
    if (ATTRIBUTION_TYPE_CHANGE_FLAG) {
        return schemaType;
    }
    if (!ATTRIBUTION_SCHEMA_TYPE_MAP[schemaType]) {
        return schemaType.toLowerCase().split('_')
            .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(' ');
    }
    return ATTRIBUTION_SCHEMA_TYPE_MAP[schemaType];
};

export const AttributionPage = () => {
    const navigate = useNavigate();
    const { attributionGuid } = useParams();
    const isEditMode = !!attributionGuid;
    const [isSaving, setIsSaving] = useState(false);
    const apiServiceContext = useContext(ApiServiceContext);
    const [saveErrorsByPointer, setSaveErrorsByPointer] = useState<ErrorsByPointer | undefined>(undefined);
    const {
        AttributionService
    } = apiServiceContext.getApiServiceCollections() as {
        AttributionService: AttributionService,
        [key: string]: ApiServiceCollection
    };
    const [formData, setFormData] = useState<Record<string, any>>({});

    const [currentAttribution, setCurrentAttribution] = useState<Omit<Attribution, 'properties'>>({
        attributionGuid: '',
        displayName: '',
        status: 'ACTIVE',
        type: schemaTypeToAttributionType(ATTRIBUTION_TYPE_DEFAULT),
        typeVersion: 0,
        propertiesVersion: 0,
        schemaType: ATTRIBUTION_TYPE_DEFAULT
    });
    const [attributionProperties, setAttributionProperties] = useState<Record<string, any>>({});
    const [attributionError, setAttributionError] = useState<string>('');
    const [loadingState, setLoadingState] = useState<LoadingState>('NOT STARTED');
    const [schemas, setSchemas] = useState<JsonSchemaType[]>([]);
    const [schemasError, setSchemasError] = useState<string>('');
    const [versions, setVersions] = useState<AttributionVersion[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<number | ''>('');

    const latestSchemas = useMemo(() => {
        const reducedSchemas = schemas.reduce((acc, cur) => {
            cur.displayName = schemaTypeToAttributionType(cur.schema_type);
            if (!acc[cur.schema_type]) {
                acc[cur.schema_type] = cur;
                return acc;
            }
            if (cur.schema_type_version > acc[cur.schema_type].schema_type_version) {
                acc[cur.schema_type] = cur;
            }
            return acc;
        }, {} as Record<string, JsonSchemaType>);
        return Object.values(reducedSchemas);
    }, [schemas]);

    const currentSchema = useMemo(() => {
        if (!latestSchemas.length) return undefined;
        const found = latestSchemas.find(
            (s) => s.schema_type === currentAttribution.schemaType
        );
        return found;
    }, [latestSchemas, currentAttribution.schemaType]);

    const validation = useMemo(() => currentSchema?.schema
        ? validateFormData(currentSchema.schema as JSONSchema7, formData, currentSchema.schema.title || 'UNKNOWN SCHEMA')
        : { isValid: true, messages: [] }
    , [currentSchema, formData]);
    const { isValid: canSave, messages: requiredDetails } = validation;

    useEffect(() => {
        setSchemasError('');
        const fetchSchemas = async () => {
            let schemasResp = await AttributionService.getAttributionSchemasByType({ type: 'attribution' });
            while (schemasResp.retry) {
                // TODO - Show retry attempts - might need to set the retry in state and loop in useEffect
                schemasResp = await schemasResp.retry.catch(error => error);
            }
            if (schemasResp.error) {
                setSchemasError('Failed to load all attribution schemas');
                return;
            }
            setSchemas(schemasResp.jsonBody!['schemas']);
        };
        fetchSchemas();
    }, [AttributionService]);

    const updateAttributionProperties = useCallback((properties: Record<string, any>) => {
        setAttributionProperties({
            ...attributionProperties,
            ...properties
        })
    }, [attributionProperties]);

    const loadAttribution = useCallback(async () => {
        setAttributionError('');

        if (!attributionGuid) {
            setLoadingState('SUCCESS');
            return;
        }

        let getAttrResp = await AttributionService.getAttributionById(attributionGuid);
        while (getAttrResp.retry) {
            getAttrResp = await getAttrResp.retry.catch(error => error);
        }
        if (getAttrResp.error) {
            setAttributionError('Failed to load selected attribution');
            setLoadingState('FAIL');
            return
        }

        let getAttrVersResp = await AttributionService.getAttributionVersions(attributionGuid);
        while (getAttrVersResp.retry) {
            getAttrVersResp = await getAttrVersResp.retry.catch(error => error);
        }
        if (getAttrVersResp.error) {
            setAttributionError('Failed to load selected attribution');
            setLoadingState('FAIL');
            return
        }

        const attributionData = getAttrResp.jsonBody;

        if (!attributionData) {
            setAttributionError('Failed to load selected attribution');
            setLoadingState('FAIL');
            return;
        }

        const { properties, ...attribution } = attributionData as Attribution;

        attribution.schemaType = attributionTypeToSchemaType(attribution.type);

        setCurrentAttribution(attribution);
        updateAttributionProperties(properties);
        setFormData({ ...properties });

        const versionsData = getAttrVersResp.jsonBody;
        if (versionsData?.data) {
            setVersions(versionsData.data);
            const latestVersion = versionsData.data.find((v: AttributionVersion) => v.latest);
            if (latestVersion) {
                setSelectedVersion(latestVersion.schema_data_version);
            }
        }

        setLoadingState('SUCCESS');
    }, [attributionGuid, AttributionService, updateAttributionProperties]);


    useEffect(() => {
        if (loadingState === 'NOT STARTED') {
            setLoadingState('LOADING');
            loadAttribution();
        }
    }, [loadingState, loadAttribution]);

    const isSuccess = (resp: any) => {
        if (!resp) return false;
        if (typeof resp.ok === 'boolean') return resp.ok;
        const status = Number(resp.status);
        return status >= 200 && status < 300;
    };

    const goBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const toErrorsByPointer = (apiErrors: Array<{ path: string; message: string }>) =>
        apiErrors.reduce((acc, { path, message }) => {
            const pointer = path.startsWith('/') ? path : `/${path.replace(/\./g, '/')}`;
            acc[pointer] = [...(acc[pointer] || []), message];
            return acc;
        }, {} as ErrorsByPointer);

    const saveAttribution = () => {
        setAttributionError('');
        setIsSaving(true);
        if (!currentSchema?.schema) {
            setSchemasError('Unable to save form. Attribution schema not loaded.');
            return;
        }
        const properties = formData;

        if (attributionGuid) {
            const { type, ...attributionUpdate } = currentAttribution;
            const updateAttributionEndpoint = AttributionService.updateAttribution;
            updateAttributionEndpoint({
                ...attributionUpdate,
                attributionGuid,
                properties
            })
                .then((resp: any) => {
                    if (!isSuccess(resp.response)) {
                        const error = new Error('Request failed');
                        (error as any).status = resp?.response?.status;
                        (error as any).data = resp?.jsonBody;
                        throw error;
                    }
                    goBack();
                })
                .catch((err) => {
                    const data = err?.data ?? err;
                    const errorsArray = data?.errors as Array<{ path: string; message: string }>;
                    const detail = data?.detail as string;

                    if (errorsArray?.length) setSaveErrorsByPointer(toErrorsByPointer(errorsArray));
                    else if (detail) setAttributionError(detail);
                    else setAttributionError('Failed to save attribution.');

                    setLoadingState('FAIL');
                })
                .finally(() => setIsSaving(false));
        } else {
            const createAttributionEndpoint = AttributionService.createAttribution;

            createAttributionEndpoint({
                ...currentAttribution,
                properties
            })
                .then((resp: any) => {
                    if (!isSuccess(resp.response)) {
                        const error = new Error('Request failed');
                        (error as any).status = resp?.response?.status;
                        (error as any).data = resp?.textBody || resp.jsonBody;
                        throw error;
                    }
                    goBack();
                })
                .catch((err) => {
                    const data = err?.data ?? err;
                    const errorsArray = data?.errors as Array<{ path: string; message: string }>;
                    const detail = data?.detail as string;

                    if (errorsArray?.length) setSaveErrorsByPointer(toErrorsByPointer(errorsArray));
                    else if (detail) setAttributionError(detail);
                    else setAttributionError('Failed to save attribution.');

                    setLoadingState('FAIL');
                })
                .finally(() => setIsSaving(false));
        }
    };

    const isPageLoading = useMemo(() => {
        return loadingState === 'NOT STARTED' || loadingState === 'LOADING' || isSaving;
    }, [loadingState, isSaving]);

    const handleAttributionTypeChange = useCallback((event: SelectChangeEvent<string>) => {
        const latest = latestSchemas.find(schema => schema.schema_type === event.target.value);
        if (!latest) {
            return;
        }
        setFormData({});
        setCurrentAttribution({
            ...currentAttribution,
            type: schemaTypeToAttributionType(latest.schema_type),
            schemaType: latest.schema_type,
            typeVersion: latest.schema_type_version
        });
    }, [latestSchemas, currentAttribution, setFormData, setCurrentAttribution]);

    const formatVersionDate = useCallback((dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    }, []);

    const handleVersionChange = useCallback((event: SelectChangeEvent<number>) => {
        const versionNumber = event.target.value as number;
        setSelectedVersion(versionNumber);

        const version = versions.find(v => v.schema_data_version === versionNumber);
        if (version) {
            setFormData({ ...version.data });
        }
    }, [versions]);

    const attributionDetailsRow = () => (
        <>
            <AppFormRow>
                <AppFormCell>
                    <FormLabel htmlFor="name" >Name</FormLabel>
                    <FormGroup row>
                        <AppTextField
                            name="name"
                            inputProps={{ "data-testid": "name" }}
                            disabled={isEditMode}
                            value={currentAttribution.displayName}
                            required
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                setCurrentAttribution({
                                    ...currentAttribution,
                                    displayName: event.target.value
                                })
                            }
                            }
                        />
                    </FormGroup>
                </AppFormCell>
                <AppFormCell>
                    <FormLabel htmlFor="status" >Status</FormLabel>
                    <FormGroup row>
                        <Select
                            id="status"
                            name="status"
                            value={currentAttribution.status}
                            onChange={(event: SelectChangeEvent<string>) => setCurrentAttribution({
                                ...currentAttribution,
                                status: event.target.value as AttributionStatus
                            })}
                        >
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="INACTIVE">Inactive</MenuItem>
                        </Select>
                    </FormGroup>
                </AppFormCell>
                <AppFormCell>
                    <FormLabel htmlFor="type" >Type</FormLabel>
                    <FormGroup row>
                        <Select
                            id="type"
                            name="type"
                            value={currentAttribution.schemaType}
                            onChange={handleAttributionTypeChange}
                            disabled={isEditMode}
                        >
                            {
                                latestSchemas.map(schema =>
                                    <MenuItem
                                        key={schema.schema_type}
                                        value={schema.schema_type}
                                    >{schema.displayName}</MenuItem>
                                )
                            }
                        </Select>
                    </FormGroup>
                </AppFormCell>
                {isEditMode && (
                    <AppFormCell>
                        {versions.length > 0 ? (
                            <>
                                <FormLabel htmlFor="version">Version</FormLabel>
                                <FormGroup row>
                                    <Select
                                        id="version"
                                        name="version"
                                        value={selectedVersion}
                                        onChange={handleVersionChange}
                                    >
                                        {versions.map(version => (
                                            <MenuItem
                                                key={version.schema_data_version}
                                                value={version.schema_data_version}
                                            >
                                                {`v${version.schema_data_version} - ${formatVersionDate(version.created_date)}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormGroup>
                            </>
                        ) : (
                            <>
                                <FormLabel htmlFor="type">Data version</FormLabel>
                                <FormGroup row>
                                    <Typography variant="body2" color="GrayText" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                                        {currentAttribution.propertiesVersion}
                                    </Typography>
                                </FormGroup>
                            </>
                        )}
                    </AppFormCell>
                )}
            </AppFormRow>
            {
                currentSchema?.schema
                    ? (
                        <SchemaFormSection
                            schema={currentSchema.schema as JSONSchema7}
                            values={formData}
                            extraErrorsByPointer={saveErrorsByPointer}
                            defaultMode='form'
                            defaultCollapsed={false}
                            onChange={setFormData}
                        />
                    )
                    : null
            }
        </>
    );

    function getFormDisplay() {
        if (loadingState === 'FAIL') {
            return <Text>{attributionError}</Text>;
        }
        if (schemasError) {
            return <Text>{schemasError}</Text>;
        }

        return (<>
            {attributionDetailsRow()}
            {requiredDetails.map(detail => <AppFormRow>
                <Text>{detail}</Text>
            </AppFormRow>)}
        </>)
    }

    return (
        <AppPage
            title="Attribution"
            isLoading={isPageLoading}
            buttonRowContent={(
                <>
                    <AppFormCell>
                        <Button variant="outlined" onClick={goBack} >Cancel</Button>
                    </AppFormCell>
                    {
                        loadingState === 'SUCCESS'
                            ? (
                                <AppFormCell>
                                    <Button
                                        disabled={!currentAttribution.displayName || !canSave || isSaving}
                                        variant="outlined"
                                        onClick={() => saveAttribution()}
                                    >Save</Button>
                                </AppFormCell>
                            )
                            : null
                    }
                </>
            )}
        >
            {getFormDisplay()}
        </AppPage>
    )
}